import { memo } from "react"
import { Button } from "@mui/material"
import { ArrowRight, Link } from "@mui/icons-material"
import PropTypes from "prop-types"
import classNames from "classnames"
import { isFunction } from "lodash"
import usePositionTooltip from "connected-components/dashboard/helpers/tooltips"
import OPTooltip from "components/material/tooltip"

const LabelWithTooltip = memo(({ children, hasPositionTooltip, label }) => {
  if (hasPositionTooltip) {
    return children
  }

  return (
    <OPTooltip title={label} placement="bottom-start" enterDelay={300}>
      {children}
    </OPTooltip>
  )
})

LabelWithTooltip.displayName = "LabelWithTooltip"

LabelWithTooltip.propTypes = {
  children: PropTypes.node.isRequired,
  hasPositionTooltip: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired,
}

const LabelCellContent = memo(({
  contributor,
  handleClickContributor,
  includeDrilldownArrows,
}) => {
  const hasChildren = !!contributor.children
  const notHeld = Boolean(contributor.notHeld)
  const isUnknown = contributor.label === "Unknown"
  const hasAction = isFunction(contributor.action) && !notHeld && !hasChildren

  // Check if position tooltip has already been applied at the children level.
  const positionTooltip = usePositionTooltip(contributor)
  const hasPositionTooltip = contributor.tooltipValue || (positionTooltip && contributor.id !== "notheld")

  const handleClick = (e) => {
    e.preventDefault()
    if (isFunction(contributor.action)) {
      contributor.action(contributor)
    } else {
      handleClickContributor(contributor.id, hasChildren)
    }
  }

  if (isUnknown) {
    return <span className="unknown">{contributor.label}</span>
  }

  if (notHeld) {
    return <span className="not-held">{contributor.label}</span>
  }

  return (
    <Button
      className={classNames("table-item-link", {
        "has-action": hasAction,
        "no-children": !hasChildren,
      })}
      onClick={handleClick}
      disableRipple={true}
    >
      <LabelWithTooltip
        hasPositionTooltip={hasPositionTooltip}
        label={contributor.label}
      >
        <span className="table-item-label">{contributor.label}</span>
        {hasAction && <Link className="external-link-icon" fontSize="small" />}
      </LabelWithTooltip>
      {includeDrilldownArrows && hasChildren && <ArrowRight />}
    </Button>
  )
})

LabelCellContent.displayName = "LabelCellContent"

LabelCellContent.propTypes = {
  contributor: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    children: PropTypes.any,
    notHeld: PropTypes.bool,
    isNoLongerHeld: PropTypes.bool,
    action: PropTypes.func,
    tooltipValue: PropTypes.any,
  }).isRequired,
  handleClickContributor: PropTypes.func.isRequired,
  includeDrilldownArrows: PropTypes.bool,
}

export default LabelCellContent
